import { ChangeDetectionStrategy, Component, ContentChild, ElementRef, EventEmitter, Input, NgZone, OnInit, Output, QueryList, TemplateRef, ViewChild, ViewChildren } from '@angular/core';
import { DateUtil } from 'src/app/shared/util/DateUtil';
import { CalendarClickEvent } from '../../models/calendar-click-event';
import { DayTemplateContext, ICalendarComponent } from '../../interface/calendar';
import { CalendarEventHandler } from '../../models/calendar-event-handler';
import { CalendarEvent } from '../../models/calendar-event';
import { DayIndexUtil } from '../../util/DayIndexUtil';
import { CalendarSettings } from '../../models/calendar-settings';

/** 
 * A year calendar.  
 * For documentation see: {@link ICalendarComponent} 
*/
@Component({
  selector: 'app-calendar-year[date]',
  templateUrl: './calendar-year.component.html',
  styleUrls: ['./calendar-year.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CalendarYearComponent implements OnInit, ICalendarComponent {
  @Input() set date(value: Date) {
    this._date = value;
    this.dates = this.getAllDaysInYear(value.getFullYear());

    this.refreshEvents();
  };
  public get date(): Date {
    return this._date;
  }

  @Input() public eventHandler: CalendarEventHandler;
  @Input() public dayTemplate: TemplateRef<DayTemplateContext>;
  @Input() public settings: CalendarSettings;

  @Output() public eventClick: EventEmitter<CalendarClickEvent> = new EventEmitter();

  @ViewChildren("yearDayItem") set yearDays(elements: QueryList<ElementRef>) {
    if (elements) {
      for (let elementRef of elements) {
        const element: HTMLElement = elementRef.nativeElement as HTMLElement;

        this.ngZone.runOutsideAngular(() => {
          element.addEventListener("mouseover", (event: MouseEvent) => {

            if (this.mouseDown) {
              element.classList.toggle("cal--selected");
              this.mouseEnterEvent(event);
            }
          })
        })
      }
    }
  }

  protected DateUtil = DateUtil;

  protected dates: Date[][] = [];

  protected eventIndex: CalendarEvent[][][] = [];

  protected today: Date;
  protected yesterday: Date;
  protected tomorrow: Date;

  private mouseDown: boolean = false;
  private _date: Date;

  constructor(private ngZone: NgZone) {
    this.today = new Date();

    this.yesterday = new Date();
    this.yesterday.setDate(this.today.getDate() - 1)
  }

  ngOnInit(): void {
    this.refreshEvents();
  }

  public refreshEvents(): void {
    this.eventIndex = this.getEvents();
  }

  protected mouseDownEvent(event: MouseEvent) {
    this.mouseDown = true;
  }

  protected mouseUpEvent(event: MouseEvent) {
    this.mouseDown = false;
  }

  protected mouseEnterEvent(event: MouseEvent) {
    console.log("Mouse enter!")
  }

  private getEvents(): CalendarEvent[][][] {
    if (!this.eventHandler)
      return;

    const startDate = this.dates.at(0).at(0);
    const endDate = this.dates.at(-1).at(-1);

    let events = this.eventHandler(startDate, endDate);

    events = events.sort((a, b) => DateUtil.sortChronological(a.startDate, a.endDate, b.startDate, b.endDate))

    let index: CalendarEvent[][][] = [];

    for (let event of events) {
      if (event.startDate.getFullYear() != startDate.getFullYear())
        continue;

      const monthIndexer = event.startDate.getMonth();
      let monthArray = index[monthIndexer];

      if (!monthArray) {
        monthArray = [];
        index[monthIndexer] = monthArray;
      }

      const dayIndexer = event.startDate.getDate() - 1;
      let dayArray = monthArray[dayIndexer];

      if (!dayArray) {
        dayArray = [];
        monthArray[dayIndexer] = dayArray;
      }

      dayArray.push(event);
    }

    return index;
  }

  private getAllDaysInYear(year: number): Date[][] {
    const dates: Date[][] = []

    const totalMonths = 12;

    for (let month = 0; month < totalMonths; month += 1) {
      let monthArray: Date[] = [];

      const endOfMonth = new Date(year, month + 1, 0)

      for (let day = 1; day <= endOfMonth.getDate(); day++) {
        monthArray.push(new Date(year, month, day));
      }

      dates.push(monthArray);
    }

    return dates;
  }

}
